import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  TrendingUp, Clock, AlertTriangle, FileText,
  Save, Check, Info, BookOpen
} from 'lucide-react';
import MathExplainer, { FormulaBlock, CalcStep, MathSection } from '../../components/MathExplainer';
import { GROUP_COLORS, ROLE_LABELS, ROLE_COLORS } from './constants';
import { formatHours, formatCost, normaliseRounding } from '../../lib/rounding';
import { getTaxLabel } from '../../lib/currency';
import { RoleMarginDialogButton } from '../../components/margin/RoleMarginDialogButton';

export function ResultsSidebar({
  results, modifiers, breakdownView, setBreakdownView,
  sortedCostByRole, sortedEffortByGroup, sortedCostByGroup,
  setShowConfidenceInfo, setShowComplexityInfo, setShowRangeInfo,
  estimateName, setEstimateName, saving, savedInfo, setSavedInfo,
  saveEstimate, savedEstimateId, isAdmin, groupChartData,
  hostingModel, hasUnsavedChanges, justSaved, navigateWithSaveGate,
  rounding,
  currencySymbol = '$', currencyCode = 'USD',
  availableCurrencies = [],
  onCurrencyChange,
  seertechEntities = [],
  entityChip = null,
  onOpenConditions,
  audRateInfo = null,
  marginInfo = null,
  taxInclusive = false,
  onTaxInclusiveChange,
}) {
  const navigate = useNavigate();
  const r = normaliseRounding(rounding);
  const hrs = (v) => formatHours(v || 0, r);
  const $$$ = (v) => formatCost(v || 0, r, currencySymbol);
  const taxLabel = getTaxLabel(currencyCode, taxInclusive);

  return (
    <div className="lg:col-span-4">
      <div className="sticky top-24 space-y-6">
        {/* Summary */}
        <Card className="bg-[#0F2040] text-white" data-testid="live-estimate-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-['Outfit'] flex items-center justify-between">
              <span className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Live Estimate
              </span>
              {results && (
                <button
                  onClick={() => navigate(`/math-reference?estimate_id=${savedEstimateId || ''}`)}
                  className="text-[10px] text-white/40 hover:text-white/70 transition-colors flex items-center gap-1 font-normal"
                  data-testid="show-all-math-btn"
                  title="Open full calculation reference"
                >
                  <BookOpen className="w-3 h-3" /> All Math
                </button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {seertechEntities.length > 0 && (
              <button
                type="button"
                onClick={onOpenConditions}
                className="w-full flex items-center justify-between gap-2 text-[10px] bg-white/10 hover:bg-white/15 rounded-md px-2 py-1.5 transition-colors"
                data-testid="estimator-entity-chip"
                title="Entity · Region · Currency — set via Project Conditions (click to edit)"
              >
                <span className="text-white/70 uppercase tracking-wider">Entity</span>
                <span className="text-white font-medium truncate max-w-[190px]">
                  {entityChip?.entity ? (entityChip.entity.code || entityChip.entity.id).toUpperCase() : 'None'}
                  {entityChip?.region ? ` · ${entityChip.region}` : ''}
                  {` · ${entityChip?.currency || currencyCode}`}
                </span>
              </button>
            )}
            {availableCurrencies.length > 0 && (
              <div className="flex items-center justify-between gap-2 text-[10px] bg-white/10 rounded-md px-2 py-1.5" data-testid="estimator-currency-picker">
                <span className="text-white/70 uppercase tracking-wider">Currency</span>
                <select
                  value={currencyCode}
                  onChange={(e) => onCurrencyChange && onCurrencyChange(e.target.value)}
                  className="bg-transparent text-white text-[11px] border border-white/20 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-white/40"
                  data-testid="estimator-currency-select"
                  title="Override the currency shown on this estimate, quote, and SOW"
                >
                  {availableCurrencies.map(c => (
                    <option
                      key={c.code}
                      value={c.code}
                      className="text-slate-900"
                    >
                      {c.code}{c.symbol ? ` (${c.symbol})` : ''} — {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {currencyCode !== 'AUD' && audRateInfo?.rate ? (
              <div className="flex items-center justify-between text-[10px] text-white/50 px-1" data-testid="estimator-fx-rate-line">
                <span>1 {currencyCode} = A${Number(audRateInfo.rate).toFixed(4)}{audRateInfo.date ? ` · snapshot ${audRateInfo.date}` : ' · live ECB'}</span>
                {results ? (
                  <span data-testid="estimator-aud-equivalent">≈ A${Math.round((results.total_cost || 0) * audRateInfo.rate).toLocaleString()}</span>
                ) : null}
              </div>
            ) : null}
            {marginInfo && (
              <div className={`text-[10px] rounded-md px-2 py-1.5 ${marginInfo.belowMin ? 'bg-rose-500/20 ring-1 ring-rose-400/50' : 'bg-white/10'}`}
                data-testid="estimator-margin-row"
                title="Leadership only — sell (AUD converted) minus internal cost rates. Configure cost rates in Admin → Settings.">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white/70 uppercase tracking-wider">Margin (A$)</span>
                  <span className={`font-semibold ${marginInfo.belowMin || marginInfo.marginAud < 0 ? 'text-rose-300' : 'text-emerald-300'}`} data-testid="estimator-margin-value">
                    A${Math.round(marginInfo.marginAud).toLocaleString()} ({marginInfo.marginPct.toFixed(1)}%)
                  </span>
                </div>
                {savedEstimateId && !hasUnsavedChanges && (
                  <div className="mt-1 flex justify-end">
                    <RoleMarginDialogButton estimateId={savedEstimateId} onApplied={() => window.location.assign(`/estimator?load=${savedEstimateId}`)} />
                  </div>
                )}
              </div>
            )}
            {marginInfo?.belowMin && (
              <div className="text-[10px] text-rose-200 bg-rose-500/15 border border-rose-400/40 rounded-md px-2 py-1.5" data-testid="estimator-margin-guardrail-warning">
                Below the {marginInfo.minMarginPct}% margin guardrail — review discounts and rate overrides before quoting.
              </div>
            )}
            {results ? (
              <>
                <div>
                  <div className="flex items-center gap-1.5 text-sm text-white/60 mb-1">
                    Total Estimated Effort
                    <MathExplainer title="Effort Calculation" variant="light" iconSize={3} testId="math-effort-info">
                      <p>Total effort is the sum of all component efforts plus PM and Testing overhead.</p>
                      <MathSection title="Per-Component Formula">
                        <FormulaBlock lines={[
                          'Component Effort =',
                          '  Base Hours \u00d7 Quantity \u00d7 Use-Case Weight',
                          '  \u00d7 Composite Modifier \u00d7 Impl Multiplier',
                          '  \u00d7 Data Integrity (Data Migration only)',
                        ]} />
                      </MathSection>
                      <MathSection title="Composite Modifier">
                        <p className="text-xs text-slate-500 mb-2">Product of all 7 project factor sliders \u00d7 hosting modifier:</p>
                        <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                          <CalcStep label="Governance" value={modifiers.governance ?? 'n/a'} />
                          <CalcStep label="Content Readiness" value={modifiers.content_readiness ?? 'n/a'} />
                          <CalcStep label="Integration Complexity" value={modifiers.integration_complexity ?? 'n/a'} />
                          <CalcStep label="Org Complexity" value={modifiers.org_complexity ?? 'n/a'} />
                          <CalcStep label="Timeline Pressure" value={modifiers.timeline_pressure ?? 'n/a'} />
                          <CalcStep label="Client Capability" value={modifiers.client_capability ?? 'n/a'} />
                          <CalcStep label="Change Readiness" value={modifiers.change_readiness ?? 'n/a'} />
                          <CalcStep label="Hosting Modifier" value={results.hosting_modifier || '1.0'} />
                          <CalcStep label="= Composite Modifier" value={results.composite_modifier} highlight bold />
                        </div>
                      </MathSection>
                      <MathSection title="Data Integrity Modifier">
                        <p className="text-xs text-slate-500 mb-2">Applied to Data Migration components only. Graduated PM & Testing overhead adjustment based on data quality level.</p>
                        <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                          <CalcStep label="Data Integrity" value={`${modifiers.data_integrity ?? 'n/a'}x`} highlight={(modifiers.data_integrity ?? 0.7) !== 1.0} />
                          {(modifiers.data_integrity ?? 0.7) !== 1.0 && (
                            <CalcStep label="PM/Testing Adjustment" value={`${results?.data_integrity_pm_testing_uplift > 0 ? '+' : ''}${results?.data_integrity_pm_testing_uplift ?? 0}% each`} />
                          )}
                        </div>
                      </MathSection>
                      <MathSection title="Overhead">
                        <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                          <CalcStep label="Sum of component efforts" value={`${Math.round(results.total_effort - (results.overhead?.pm_hours || 0) - (results.overhead?.testing_hours || 0))} hrs`} />
                          <CalcStep label="PM Overhead (20%)" value={`${Math.round(results.overhead?.pm_hours || 0)} hrs`} />
                          <CalcStep label="Testing Overhead (15%)" value={`${Math.round(results.overhead?.testing_hours || 0)} hrs`} />
                          <CalcStep label="= Total Effort" value={`${hrs(results.total_effort)} hrs`} highlight bold />
                        </div>
                      </MathSection>
                      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
                        <strong>Note:</strong> Volume-based components use per-volume rates instead of base hours. Hour overrides bypass the standard formula.
                      </div>
                    </MathExplainer>
                  </div>
                  <div className="text-3xl font-bold font-['Outfit']" data-testid="total-effort">
                    {hrs(results.total_effort)} hrs
                  </div>
                  <div className="text-sm text-white/60">
                    {results.effort_range?.low?.toLocaleString()} - {results.effort_range?.high?.toLocaleString()} hrs
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center gap-1.5 text-sm text-white/60 mb-1">
                    Total Estimated Cost
                    <MathExplainer title="Cost Calculation" variant="light" iconSize={3} testId="math-cost-info">
                      <p>Total cost is derived from effort hours multiplied by role-specific hourly rates, plus overhead costs.</p>
                      <MathSection title="Per-Component Cost">
                        <FormulaBlock lines={[
                          'Component Cost = Final Effort \u00d7 Role Rate ($/hr)',
                        ]} />
                      </MathSection>
                      <MathSection title="Cost Breakdown">
                        <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                          {sortedCostByRole.map(([role, cost]) => (
                            <CalcStep key={role} label={`${role} role`} value={`${currencySymbol}${Math.round(cost).toLocaleString()}`} />
                          ))}
                          <CalcStep label="PM Overhead" value={`${currencySymbol}${Math.round(results.overhead?.pm_cost || 0).toLocaleString()}`} />
                          <CalcStep label="Testing Overhead" value={`${currencySymbol}${Math.round(results.overhead?.testing_cost || 0).toLocaleString()}`} />
                          <CalcStep label="= Total Cost" value={`${$$$(results.total_cost)}`} highlight bold />
                        </div>
                      </MathSection>
                      <MathSection title="Overhead Cost Formulas">
                        <FormulaBlock lines={[
                          `PM Overhead Cost = ${Math.round(results.overhead?.pm_hours || 0)} hrs \u00d7 PM Rate`,
                          `Testing Overhead = ${Math.round(results.overhead?.testing_hours || 0)} hrs \u00d7 CF Rate`,
                        ]} />
                      </MathSection>
                    </MathExplainer>
                  </div>
                  <div className="text-3xl font-bold text-[#4ABFCF] font-['Outfit']" data-testid="total-cost">
                    {$$$(results.total_cost)}
                  </div>
                  {/* Tax descriptor subline — purely informational, no calc.
                      Click to toggle Excluding↔Including {GST|VAT|Taxes}. */}
                  <button
                    type="button"
                    onClick={() => onTaxInclusiveChange && onTaxInclusiveChange(!taxInclusive)}
                    className="text-[11px] text-white/55 hover:text-white/85 transition-colors flex items-center gap-1 mt-0.5"
                    data-testid="estimator-tax-label-toggle"
                    title="Click to toggle inclusive/exclusive label"
                  >
                    {taxLabel.applied}
                    <span className="text-white/30 text-[10px]">·</span>
                    <span className="text-white/40 text-[10px] uppercase tracking-wide">click to toggle</span>
                  </button>
                  <button onClick={() => setShowRangeInfo(true)} className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white/80 transition-colors cursor-help" data-testid="range-info-btn">
                    {currencySymbol}{results.cost_range?.low?.toLocaleString()} - {currencySymbol}{results.cost_range?.high?.toLocaleString()}
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/20">
                  <div>
                    <button onClick={() => setShowConfidenceInfo(true)} className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white/80 transition-colors cursor-help" data-testid="confidence-info-btn">
                      Confidence
                      <Info className="w-3.5 h-3.5" />
                    </button>
                    <div className="text-xl font-semibold" data-testid="confidence-score">{results.confidence}%</div>
                  </div>
                  <div>
                    <button onClick={() => setShowComplexityInfo(true)} className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white/80 transition-colors cursor-help" data-testid="complexity-info-btn">
                      Complexity
                      <Info className="w-3.5 h-3.5" />
                    </button>
                    <div className="text-xl font-semibold" data-testid="complexity-label">{results.complexity_label}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-6 text-white/60">
                <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>Select components to see estimate</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Breakdown */}
        {results && groupChartData.length > 0 && (
          <Card data-testid="effort-breakdown-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Breakdown</CardTitle>
              </div>
              {/* Tab switcher */}
              <div className="flex bg-slate-100 rounded-lg p-0.5 mt-2" data-testid="breakdown-tabs">
                {[
                  { key: 'hours', label: 'Hours' },
                  { key: 'cost', label: 'Cost' },
                  { key: 'roles', label: 'Functions' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setBreakdownView(tab.key)}
                    data-testid={`breakdown-tab-${tab.key}`}
                    className={`flex-1 text-xs font-medium py-1.5 px-2 rounded-md transition-all ${
                      breakdownView === tab.key
                        ? 'bg-white text-[#0F2040] shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Hours view */}
              {breakdownView === 'hours' && (
                <div className="space-y-2.5" data-testid="breakdown-hours">
                  {sortedEffortByGroup.map(([group, hours]) => {
                    const pct = ((hours / results.total_effort) * 100);
                    return (
                      <div key={group} className="group" data-testid={`hours-row-${group}`}>
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-medium text-slate-700 truncate max-w-[140px]">{group}</span>
                          <span className="text-xs text-slate-500 ml-2 whitespace-nowrap">{Math.round(hours)}h &middot; {pct.toFixed(0)}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, backgroundColor: GROUP_COLORS[group] || '#64748B' }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {/* Overhead */}
                  {results.overhead && (
                    <div className="pt-2 mt-2 border-t border-slate-100 space-y-2">
                      <div data-testid="hours-row-pm-overhead">
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-medium text-slate-400">PM Overhead</span>
                          <span className="text-xs text-slate-400">{Math.round(results.overhead.pm_hours)}h</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-slate-300" style={{ width: `${(results.overhead.pm_hours / results.total_effort) * 100}%` }} />
                        </div>
                      </div>
                      <div data-testid="hours-row-testing-overhead">
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-medium text-slate-400">Testing Overhead</span>
                          <span className="text-xs text-slate-400">{Math.round(results.overhead.testing_hours)}h</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-slate-300" style={{ width: `${(results.overhead.testing_hours / results.total_effort) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cost view */}
              {breakdownView === 'cost' && (
                <div className="space-y-2.5" data-testid="breakdown-cost">
                  {sortedCostByGroup.map(([group, cost]) => {
                    const pct = ((cost / results.total_cost) * 100);
                    return (
                      <div key={group} className="group" data-testid={`cost-row-${group}`}>
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-medium text-slate-700 truncate max-w-[140px]">{group}</span>
                          <span className="text-xs text-slate-500 ml-2 whitespace-nowrap">{currencySymbol}{cost.toLocaleString(undefined, {maximumFractionDigits: 0})} &middot; {pct.toFixed(0)}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, backgroundColor: GROUP_COLORS[group] || '#64748B' }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {/* Overhead costs */}
                  {results.overhead && (
                    <div className="pt-2 mt-2 border-t border-slate-100 space-y-2">
                      <div data-testid="cost-row-pm-overhead">
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-medium text-slate-400">PM Overhead</span>
                          <span className="text-xs text-slate-400">{currencySymbol}{results.overhead.pm_cost.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-slate-300" style={{ width: `${(results.overhead.pm_cost / results.total_cost) * 100}%` }} />
                        </div>
                      </div>
                      <div data-testid="cost-row-testing-overhead">
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-medium text-slate-400">Testing Overhead</span>
                          <span className="text-xs text-slate-400">{currencySymbol}{results.overhead.testing_cost.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-slate-300" style={{ width: `${(results.overhead.testing_cost / results.total_cost) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Roles view */}
              {breakdownView === 'roles' && (
                <div className="space-y-2.5" data-testid="breakdown-roles">
                  {Object.entries(results.effort_by_role || {})
                    .filter(([, hrs]) => hrs > 0)
                    .sort((a, b) => b[1] - a[1])
                    .map(([role, hours]) => {
                      const roleCost = results.cost_by_role?.[role] || 0;
                      const pct = ((hours / results.total_effort) * 100);
                      return (
                        <div key={role} className="group" data-testid={`role-row-${role}`}>
                          <div className="flex justify-between items-baseline mb-1">
                            <span className="text-xs font-medium text-slate-700">{ROLE_LABELS[role] || role}</span>
                            <span className="text-xs text-slate-500 whitespace-nowrap">{Math.round(hours)}h &middot; {currencySymbol}{roleCost.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${pct}%`, backgroundColor: ROLE_COLORS[role] || '#64748B' }}
                            />
                          </div>
                          <span className="text-[10px] text-slate-400">{pct.toFixed(0)}% of effort</span>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Risks */}
        {results?.risks?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Risks
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {results.risks.slice(0, 3).map((risk, idx) => (
                <div 
                  key={`risk-${risk.type}-${idx}`} 
                  title={risk.detail || undefined}
                  className={`p-2 rounded text-xs ${risk.detail ? 'cursor-help' : ''} ${
                    risk.type === 'high' ? 'bg-red-50 text-red-700' :
                    risk.type === 'medium' ? 'bg-amber-50 text-amber-700' :
                    'bg-blue-50 text-blue-700'
                  }`}
                >
                  {risk.message}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions (gated) */}
        {results && (
          <Card data-testid="actions-card">
            <CardContent className="p-4 space-y-3">
              {savedEstimateId && !hasUnsavedChanges ? (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="saved-confirmation">
                  <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-emerald-700 truncate block">{estimateName || 'Saved'}</span>
                    {savedInfo?.time && (
                      <span className="text-[10px] text-emerald-500">
                        Saved {new Date(savedInfo.time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-amber-600 text-center py-1 font-medium">
                  Save your estimate to unlock full reporting & planning
                </p>
              )}

              <Button 
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                onClick={() => navigateWithSaveGate('/workspace/{id}/overview')}
                data-testid="view-full-report-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Full Report
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
